import useSWR, { SWRConfiguration, mutate } from 'swr';
import fetchDataService from '../services/fetchDataService';
import { useConfig } from '../contexts/ConfigContext';
import useAuth from './useAuth';
import {
  JournalEntry,
  AccountItem,
  AccountingConfigItem,
  AccountingPeriodLock,
} from '../services/accountingService';

/**
 * Helper to safely extract array collections from various response shapes.
 */
export const getArrayData = (data: any, key: string): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data[key])) return data[key];
  if (Array.isArray(data.data)) return data.data;
  if (data.data && Array.isArray(data.data[key])) return data.data[key];
  return [];
};

/**
 * Hook for fetching journal entries with date range and company filtering.
 */
export function useJournalEntries(
  params: {
    company_id?: number;
    start_date?: string;
    end_date?: string;
    all?: boolean;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;
  const startDate = params.start_date;
  const endDate = params.end_date;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/journal-entries', companyId, startDate, endDate, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/journal-entries', activeKey, {
      company_id: companyId,
      start_date: startDate,
      end_date: endDate,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: true,
    ...options,
  });

  const entries: JournalEntry[] = getArrayData(data, 'entries');

  return {
    entries,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching operational movements that are pending or failed in accounting.
 */
export function usePendingAccountingMovements(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/pending-movements', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/pending-movements', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: true,
    ...options,
  });

  const pendingMovements: any[] = getArrayData(data, 'movements');

  return {
    pendingMovements,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching chart of accounts (PUC).
 */
export function useAccounts(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/accounts', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/accounts', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const accounts: AccountItem[] = getArrayData(data, 'accounts');

  return {
    accounts,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching company accounting period lock.
 */
export function usePeriodLock(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/period-lock', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/period-lock', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const rawLock = data?.lock || data?.data?.lock || data?.data || data;
  const lock: AccountingPeriodLock | null = rawLock?.lock_date ? rawLock : null;
  const lockDate: string | null = lock?.lock_date ? String(lock.lock_date).substring(0, 10) : null;

  return {
    lock,
    lockDate,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching contacts/third-parties.
 */
export function useContacts(
  params: {
    company_id?: number;
    company_uid?: string;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/contacts/get-contacts', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/contacts/get-contacts', activeKey, {
      company_id: companyId,
      company: params.company_uid || companyId,
      all: true,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const contacts: any[] = getArrayData(data, 'contactList');

  return {
    contacts,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Hook for fetching accounting configuration mappings.
 */
export function useAccountingConfigs(
  params: {
    company_id?: number;
  },
  options?: SWRConfiguration
) {
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const companyId = params.company_id;

  const activeKey = tokenData?.key || config.key;
  const activeToken = tokenData?.token?.uid || config.token;

  const swrKey = activeKey && companyId
    ? ['/accounting/configs', companyId, activeToken]
    : null;

  const fetcher = async () => {
    return await fetchDataService.post('/accounting/configs', activeKey, {
      company_id: companyId,
      csrfToken: activeToken,
    });
  };

  const { data, error, isLoading, isValidating, mutate: boundMutate } = useSWR(swrKey, fetcher, {
    dedupingInterval: 30000,
    revalidateOnFocus: true,
    ...options,
  });

  const configs: AccountingConfigItem[] = getArrayData(data, 'configs');

  return {
    configs,
    rawData: data,
    isLoading: isLoading || (!data && !error && Boolean(swrKey)),
    isValidating,
    error,
    mutate: boundMutate,
  };
}

/**
 * Global mutation helpers to trigger background revalidations anywhere in the application.
 */
export const mutateJournalEntries = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/journal-entries' && (!companyId || key[1] === companyId));

export const mutatePendingAccountingMovements = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/pending-movements' && (!companyId || key[1] === companyId));

export const mutateAccounts = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/accounts' && (!companyId || key[1] === companyId));

export const mutatePeriodLock = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/period-lock' && (!companyId || key[1] === companyId));

export const mutateAccountingConfigs = (companyId?: number) =>
  mutate((key: any) => Array.isArray(key) && key[0] === '/accounting/configs' && (!companyId || key[1] === companyId));
